import { Controller } from '@hotwired/stimulus'
import { createApp } from 'vue'
import consumer from '~/channels/consumer'

/**
 * Generation Lab Controller
 *
 * Stimulus controller for the Generation Lab index page.
 * Mounts the Vue app for creating new generation runs.
 */
export default class extends Controller {
  static targets = ['loading']

  static values = {
    industries: Object,
    pageTypes: Array,
    imageProviders: Array,
    csrfToken: String,
    createUrl: String
  }

  connect() {
    this.vueApp = null
    this.subscription = null
    this.mountVue()
  }

  disconnect() {
    this.unmountVue()
    this.unsubscribe()
  }

  async mountVue() {
    if (this.vueApp) return

    // Hide loading spinner
    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = 'none'
    }

    // Dynamic import of the Vue component
    const { default: GenerationLabIndex } = await import('~/javascript/components/admin/generation_lab/GenerationLabIndex.vue')

    const props = {
      industries: this.industriesValue || {},
      pageTypes: this.pageTypesValue || [],
      imageProviders: this.imageProvidersValue || [],
      csrfToken: this.csrfTokenValue || document.querySelector('meta[name="csrf-token"]')?.content,
      createUrl: this.createUrlValue,
      onRunCreated: (run) => this.handleRunCreated(run)
    }

    this.vueApp = createApp(GenerationLabIndex, props)
    this.vueApp.mount(this.element)
  }

  unmountVue() {
    if (this.vueApp) {
      this.vueApp.unmount()
      this.vueApp = null
    }
  }

  handleRunCreated(run) {
    // Subscribe to the new run's channel for real-time updates
    this.subscribeToRun(run.id)

    // Redirect to the show page
    if (run.redirect_url) {
      window.location.href = run.redirect_url
    }
  }

  subscribeToRun(runId) {
    this.unsubscribe()

    this.subscription = consumer.subscriptions.create(
      { channel: 'GenerationLabChannel', run_id: runId },
      {
        received: (data) => {
          // Handle real-time updates
          if (data.event === 'run_completed') {
            // Refresh the page or update the UI
            window.location.reload()
          }
        }
      }
    )
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }
}
